// UserLocationOverlay.js
// User-location marker implemented as an Apple MapKit JS custom DOM annotation.
// Keeps the interface Map.jsx expects:
//   new UserLocationOverlay(position, map) / updatePosition(pos) / setMap(null)

import {
  safeAddAnnotation,
  registerAnnotationElement,
  unregisterAnnotationElement,
} from '../../services/appleMapKit';

export const createUserLocationOverlayClass = (mapkit) => {
  const mk = mapkit || window.mapkit;

  class UserLocationOverlay {
    constructor(position, map) {
      this.position = position;
      this.map = map;

      // Outer container (MapKit centers it on the coordinate)
      const div = document.createElement('div');
      div.style.width = '20px';
      div.style.height = '20px';
      div.style.position = 'relative';
      div.style.pointerEvents = 'none'; // Don't block map interactions

      // Pulsing outer circle
      const pulsingCircle = document.createElement('div');
      pulsingCircle.style.position = 'absolute';
      pulsingCircle.style.top = '50%';
      pulsingCircle.style.left = '50%';
      pulsingCircle.style.width = '20px';
      pulsingCircle.style.height = '20px';
      pulsingCircle.style.backgroundColor = 'rgba(66, 165, 245, 0.3)';
      pulsingCircle.style.borderRadius = '50%';
      pulsingCircle.style.transform = 'translate(-50%, -50%)';
      pulsingCircle.style.animation = 'userLocationPulse 2s infinite';

      // Inner solid circle
      const innerCircle = document.createElement('div');
      innerCircle.style.position = 'absolute';
      innerCircle.style.top = '50%';
      innerCircle.style.left = '50%';
      innerCircle.style.width = '12px';
      innerCircle.style.height = '12px';
      innerCircle.style.backgroundColor = '#1976d2';
      innerCircle.style.border = '2px solid white';
      innerCircle.style.borderRadius = '50%';
      innerCircle.style.transform = 'translate(-50%, -50%)';
      innerCircle.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

      div.appendChild(pulsingCircle);
      div.appendChild(innerCircle);
      this.div = div;
      // Registered so the info-box stacking logic can recognise MapKit's shared
      // annotation layer (any node containing a *different* annotation).
      registerAnnotationElement(div);

      this.annotation = new mk.Annotation(
        new mk.Coordinate(position.lat, position.lng),
        () => this.div,
        { animates: false }
      );

      // Guarded: the map element may not be rooted yet (or may already be gone
      // after a view switch), which makes MapKit throw on addAnnotation.
      safeAddAnnotation(map, this.annotation);
    }

    updatePosition(newPosition) {
      this.position = newPosition;
      if (!this.annotation) return;
      try {
        this.annotation.coordinate = new mk.Coordinate(newPosition.lat, newPosition.lng);
      } catch (e) {
        /* annotation detached */
      }
    }

    setMap(map) {
      if (map === null) {
        unregisterAnnotationElement(this.div);
        if (this.map && this.annotation) {
          try {
            this.map.removeAnnotation(this.annotation);
          } catch (e) {
            /* annotation already removed */
          }
        }
        this.map = null;
        return;
      }
      this.map = map;
      if (this.annotation) safeAddAnnotation(map, this.annotation);
    }
  }

  return UserLocationOverlay;
};
